import React, { useState } from 'react';

const HelpPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('general');
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const faqCategories = [
    { id: 'general', label: 'General Questions' },
    { id: 'setup', label: 'Setup & Installation' },
    { id: 'billing', label: 'Billing & Plans' },
    { id: 'technical', label: 'Technical Support' },
    { id: 'emergency', label: 'Emergency Procedures' }
  ];

  const faqs = {
    general: [
      {
        question: "How does Alert1 work?",
        answer: "Alert1 provides 24/7 monitoring through our certified monitoring centers. When you press your emergency button, you're instantly connected to a trained operator who will assess your situation and dispatch appropriate help - whether that's family, friends, or emergency services."
      },
      {
        question: "What's the difference between in-home and mobile systems?",
        answer: "In-home systems work within and around your home (1000+ foot range) and include a base station and pendant. Mobile systems use GPS and cellular technology to protect you anywhere you go, with nationwide coverage."
      },
      {
        question: "Do I need a landline phone?",
        answer: "No! Our systems work with landlines, cell phones, or VoIP services. We also offer cellular-based systems that don't require any phone service at all."
      },
      {
        question: "What happens if I accidentally press the button?",
        answer: "No problem! Our operators will ask for your safety word. If you provide it, they'll know it was accidental and no help will be sent. There's no charge for false alarms."
      }
    ],
    setup: [
      {
        question: "How long does setup take?",
        answer: "Most customers can set up their system in under 10 minutes. We provide simple, step-by-step instructions, and our customer service team is available to walk you through the process if needed."
      },
      {
        question: "Do I need any special equipment or internet?",
        answer: "No special equipment needed! Our in-home systems work with your existing phone line or can use cellular service. Mobile systems are completely self-contained."
      },
      {
        question: "Can I test my system?",
        answer: "Yes! We encourage monthly testing. Simply press and hold your button for 5 seconds during business hours, and our operators will confirm your system is working properly."
      },
      {
        question: "What if my power goes out?",
        answer: "Our in-home base stations have backup batteries that last up to 24 hours. Mobile devices have their own rechargeable batteries lasting 5+ days on a single charge."
      }
    ],
    billing: [
      {
        question: "Are there any setup fees or contracts?",
        answer: "No setup fees and no long term contract required. Any user may cancel service at any time by returning the Alert1 equipment. If you are on a contract of a specified length (e.g. annual) for a discount, you may cancel early but Alert1 does not offer refunds except in the case of death or a move to a certified nursing home or assisted living facility where Alert1 service is no longer supported or allowed."
      },
      {
        question: "What payment methods do you accept?",
        answer: "We accept all major credit cards, bank transfers, and can set up automatic monthly billing for your convenience. We also offer discounts for annual payments."
      },
      {
        question: "Do you offer discounts for seniors or veterans?",
        answer: "Yes! We offer special pricing for seniors 65+, veterans, and multiple family members. Call us at 1-800-693-5433 to learn about current promotions."
      },
      {
        question: "What's included in my monthly fee?",
        answer: "Everything! 24/7 monitoring, unlimited emergency calls, family notifications, equipment maintenance, customer support, and free battery replacements."
      }
    ],
    technical: [
      {
        question: "My pendant isn't working. What should I do?",
        answer: "First, check if the battery needs replacing (we send free replacements). Try pressing the button - you should see a light and hear a beep. If not, call our technical support at 1-800-693-5433."
      },
      {
        question: "How do I know if my system is working?",
        answer: "Your base station will show green lights when everything is working properly. Mobile devices display signal strength and battery level. We also send monthly system status reports."
      },
      {
        question: "What's the range of my in-home system?",
        answer: "Our in-home systems typically work up to 1000+ feet from the base station, covering most homes and extending into yards, garages, and basements."
      },
      {
        question: "Can I wear my pendant in the shower?",
        answer: "Yes! All our pendants are waterproof and designed to be worn 24/7, including in the shower, bath, or pool."
      }
    ],
    emergency: [
      {
        question: "What happens when I press my button?",
        answer: "You'll be connected to our monitoring center within 30 seconds. Our trained operators will speak with you to assess the situation and coordinate the appropriate response."
      },
      {
        question: "What if I can't speak during an emergency?",
        answer: "If you don't respond or seem to be in distress, our operators will immediately dispatch emergency services to your location using the information in your profile."
      },
      {
        question: "Will my family be notified?",
        answer: "Yes! We'll contact your designated emergency contacts to let them know about the situation and what actions we've taken on your behalf."
      },
      {
        question: "Do you work with my local emergency services?",
        answer: "Absolutely! We work directly with 911 dispatch centers and can provide them with your medical information, emergency contacts, and any special instructions you've provided."
      }
    ]
  };

  const supportOptions = [
    {
      title: "24/7 Phone Support",
      description: "Speak with our customer service team anytime",
      contact: "1-800-693-5433",
      icon: "📞",
      available: "Available 24/7"
    },
    {
      title: "Live Chat",
      description: "Get instant help from our support team",
      contact: "Start Chat",
      icon: "💬",
      available: "Mon-Fri 8AM-8PM EST"
    },
    {
      title: "Email Support",
      description: "Send us your questions and we'll respond quickly",
      contact: "support@alert1.com",
      icon: "📧",
      available: "Response within 24 hours"
    },
    {
      title: "Video Tutorials",
      description: "Watch step-by-step setup and usage guides",
      contact: "View Library",
      icon: "🎥",
      available: "Available anytime"
    }
  ];

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value
    });
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Contact form submitted:', contactForm);
    alert('Thank you for your message! We\'ll get back to you within 24 hours.');
    setContactForm({ name: '', email: '', phone: '', subject: '', message: '' });
  };

  return (
    <div className="help-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">How Can We Help You?</h1>
            <p className="hero-subtitle">
              Find answers to common questions, get technical support, or contact our customer service team. 
              We're here to help 24/7.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Support Options */}
      <section className="quick-support-section">
        <div className="container">
          <h2 className="section-title">Get Help Now</h2>
          <div className="support-options-grid">
            {supportOptions.map((option, index) => (
              <div key={index} className="support-option-card">
                <div className="support-icon">{option.icon}</div>
                <h3 className="support-title">{option.title}</h3>
                <p className="support-description">{option.description}</p>
                <div className="support-contact">{option.contact}</div>
                <div className="support-availability">{option.available}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          
          {/* FAQ Categories */}
          <div className="faq-categories">
            {faqCategories.map((category) => (
              <button
                key={category.id}
                className={`faq-category-btn ${selectedCategory === category.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.label}
              </button>
            ))}
          </div>

          {/* FAQ Items */}
          <div className="faq-items">
            {faqs[selectedCategory as keyof typeof faqs].map((faq, index) => (
              <div key={index} className="faq-item">
                <h3 className="faq-question">{faq.question}</h3>
                <p className="faq-answer">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* User Guides Section */}
      <section className="guides-section">
        <div className="container">
          <h2 className="section-title">User Guides & Resources</h2>
          <div className="guides-grid">
            <div className="guide-card">
              <div className="guide-icon">📖</div>
              <h3>Quick Start Guide</h3>
              <p>Get your system up and running in minutes with our step-by-step setup guide.</p>
              <button className="btn btn-outline">Download PDF</button>
            </div>
            
            <div className="guide-card">
              <div className="guide-icon">🎥</div>
              <h3>Video Tutorials</h3>
              <p>Watch our comprehensive video library covering setup, testing, and troubleshooting.</p>
              <button className="btn btn-outline">Watch Videos</button>
            </div>
            
            <div className="guide-card">
              <div className="guide-icon">🔧</div>
              <h3>Troubleshooting Guide</h3>
              <p>Solve common issues quickly with our detailed troubleshooting instructions.</p>
              <button className="btn btn-outline">View Guide</button>
            </div>
            
            <div className="guide-card">
              <div className="guide-icon">📱</div>
              <h3>Mobile App Guide</h3>
              <p>Learn how to use the Alert1 family app to stay connected with your loved ones.</p>
              <button className="btn btn-outline">Get App</button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-form-section">
        <div className="container">
          <div className="contact-content">
            <div className="contact-info">
              <h2 className="section-title">Still Need Help?</h2>
              <p className="contact-description">
                Can't find what you're looking for? Our customer service team is standing by 
                to help with any questions or concerns you may have.
              </p>
              
              <div className="contact-methods">
                <div className="contact-method">
                  <div className="contact-method-icon">📞</div>
                  <div className="contact-method-info">
                    <h4>Call Us</h4>
                    <p>1-800-693-5433</p>
                    <span>Available 24/7</span>
                  </div>
                </div>
                
                <div className="contact-method">
                  <div className="contact-method-icon">📧</div>
                  <div className="contact-method-info">
                    <h4>Email Us</h4>
                    <p>support@alert1.com</p>
                    <span>Response within 24 hours</span>
                  </div>
                </div>
                
                <div className="contact-method">
                  <div className="contact-method-icon">💬</div>
                  <div className="contact-method-info">
                    <h4>Live Chat</h4>
                    <p>Instant messaging support</p>
                    <span>Mon-Fri 8AM-8PM EST</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="contact-form">
              <h3>Send Us a Message</h3>
              <form onSubmit={handleFormSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={contactForm.name}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={contactForm.email}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={contactForm.phone}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="subject">Subject *</label>
                    <select
                      id="subject"
                      name="subject"
                      value={contactForm.subject}
                      onChange={handleFormChange}
                      required
                    >
                      <option value="">Select a topic</option>
                      <option value="technical">Technical Support</option>
                      <option value="billing">Billing Question</option>
                      <option value="setup">Setup Help</option>
                      <option value="general">General Question</option>
                      <option value="complaint">Complaint</option>
                      <option value="compliment">Compliment</option>
                    </select>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="message">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    value={contactForm.message}
                    onChange={handleFormChange}
                    placeholder="Please describe your question or issue in detail..."
                    required
                  ></textarea>
                </div>
                
                <button type="submit" className="btn btn-primary btn-large">
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Notice */}
      <section className="emergency-notice">
        <div className="container">
          <div className="notice-content">
            <div className="notice-icon">🚨</div>
            <div className="notice-text">
              <h3>In Case of Emergency</h3>
              <p>
                If you're experiencing a medical emergency, please call 911 immediately or press your Alert1 button. 
                This contact form is for non-emergency support only.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HelpPage;
